/**
 * Phone layout of the product video blocks: one card per block, each clip playing only while its
 * card is on screen.
 *
 * Nothing loads up front. Every clip ships with preload="none", so a card that is never scrolled to
 * costs its poster and nothing else, and a card that leaves the screen is paused rather than left to
 * keep pulling the file.
 */

class ProductVideoCards extends HTMLElement {
    connectedCallback() {
        this.clips = Array.from(this.querySelectorAll('video'));
        if (this.clips.length === 0) return;

        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.blocked = false;

        this.observer = new IntersectionObserver((entries) => this.onIntersect(entries), {
            threshold: 0.5,
        });
        this.clips.forEach((clip) => this.observer.observe(clip));

        // Coming back to the tab fires no intersection change, and the browser will have paused the
        // clip behind our back.
        this.onVisibility = () => {
            if (document.visibilityState === 'visible') this.visible.forEach((clip) => this.play(clip));
        };
        this.visible = new Set();
        document.addEventListener('visibilitychange', this.onVisibility);
    }

    disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        document.removeEventListener('visibilitychange', this.onVisibility);
    }

    onIntersect(entries) {
        entries.forEach((entry) => {
            const clip = entry.target;
            // offsetParent is null while the whole layout sits behind the desktop media query, and
            // a clip started there would download in full for nobody.
            if (entry.isIntersecting && clip.offsetParent !== null) {
                this.visible.add(clip);
                this.play(clip);
            } else {
                this.visible.delete(clip);
                if (!clip.paused) clip.pause();
            }
        });
    }

    play(clip) {
        if (this.reducedMotion || this.blocked || !clip.paused) return;
        clip.preload = 'auto';
        const played = clip.play();
        if (played && typeof played.catch === 'function') {
            played.catch((error) => {
                if (error && error.name === 'AbortError') return;
                // Low Power Mode or a site set never to autoplay. Hand the reader the controls.
                if (error && error.name === 'NotAllowedError') {
                    this.blocked = true;
                    this.clips.forEach((c) => (c.controls = true));
                    return;
                }
                console.error('[ProductVideoCards] clip will not play', clip.currentSrc || '(no source)', error);
            });
        }
    }
}

if (!customElements.get('product-video-cards')) {
    customElements.define('product-video-cards', ProductVideoCards);
}

export { ProductVideoCards };
